import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

import { EmptyTable } from "@/components/Datatable/components/EmptyTable";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableLoad } from "@/components/ui/table-load";
import { cn } from "@/lib/utils";
import { PendingStore } from "@/services/price-remark/types";
import { formatNumber } from "@/utils/formatToMoney";

import { AGE_ALERT_DAYS, IDLE_ALERT_DAYS } from "../constants";
import { formatBatchShort, pluralizeDays } from "../format";
import { BatchStatusBadge, StoreStatusBadge } from "./situation-badge";

const COLUMN_COUNT = 10;

interface NetworkTableProps {
  stores: PendingStore[];
  isLoading: boolean;
  regionByStore: Map<number, string>;
  onOpenStore: (storeCode: number) => void;
}

function IdleCell({ days }: { days: number | null }) {
  if (days == null) {
    return <span className="font-bold text-red-600">nunca emitiu</span>;
  }
  if (days === 0) return <span className="text-gray-500">hoje</span>;

  return (
    <span
      className={cn(
        "text-gray-500",
        days >= IDLE_ALERT_DAYS && "font-semibold text-red-600",
      )}
    >
      {pluralizeDays(days)}
    </span>
  );
}

function BatchLines({
  store,
  onOpenStore,
}: {
  store: PendingStore;
  onOpenStore: (storeCode: number) => void;
}) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={COLUMN_COUNT} className="h-auto bg-gray-50/70">
        <div className="my-4 ml-8 border-l-2 border-gray-300 pl-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Lotes na janela
          </p>
          <div className="space-y-1.5">
            {store.batches.map((batch) => (
              <div
                key={batch.batch}
                className="grid grid-cols-[7rem_9rem_8rem_10rem_1fr] items-center gap-3 text-xs"
              >
                <span className="font-mono font-semibold text-gray-700">
                  {formatBatchShort(batch.batch)}
                </span>
                <span className="text-gray-600">
                  {formatNumber(batch.items)} a remarcar
                </span>
                <span
                  className={cn(
                    "text-gray-600",
                    batch.touched === 0 && "font-semibold text-red-600",
                  )}
                >
                  {formatNumber(batch.touched)} tocados
                </span>
                <BatchStatusBadge status={batch.status} />
                <span
                  className={cn(
                    "text-gray-500",
                    batch.ageDays >= AGE_ALERT_DAYS && "font-semibold text-red-600",
                  )}
                >
                  {pluralizeDays(batch.ageDays)}
                </span>
              </div>
            ))}
          </div>
          <Button
            size="sm"
            className="mb-1 mt-4 h-8"
            onClick={() => onOpenStore(store.storeCode)}
          >
            Abrir detalhe da loja
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

// A MonitoringTable não suporta expansão de linha, então ela mora aqui em vez
// de alterar o componente compartilhado do portal.
export function NetworkTable({
  stores,
  isLoading,
  regionByStore,
  onOpenStore,
}: NetworkTableProps) {
  const [expanded, setExpanded] = useState<number | null>(null);

  const toggle = (storeCode: number) =>
    setExpanded((current) => (current === storeCode ? null : storeCode));

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader className="bg-[#F9FBFC]">
          <TableRow>
            <TableHead className="text-center">Loja</TableHead>
            <TableHead className="text-center">Regional / UF</TableHead>
            <TableHead className="text-center">Lotes</TableHead>
            <TableHead className="text-center">Itens a remarcar</TableHead>
            <TableHead className="text-center">Não iniciados</TableHead>
            <TableHead className="text-center">Etiquetas</TableHead>
            <TableHead className="text-center">Última emissão</TableHead>
            <TableHead className="text-center">Parada há</TableHead>
            <TableHead className="text-center">Situação</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableLoad colSpan={COLUMN_COUNT} />
          ) : stores.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT} className="h-24 text-center">
                <EmptyTable text="Nenhuma loja nesta situação." />
              </TableCell>
            </TableRow>
          ) : (
            stores.map((store) => {
              const isOpen = expanded === store.storeCode;
              return [
                <TableRow
                  key={store.storeCode}
                  tabIndex={0}
                  className={cn(
                    "cursor-pointer",
                    isOpen && "bg-gray-50/70 hover:bg-gray-50",
                  )}
                  onClick={() => toggle(store.storeCode)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      toggle(store.storeCode);
                    }
                  }}
                >
                  <TableCell className="whitespace-nowrap text-center">
                    <span className="flex items-center gap-1.5">
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                      )}
                      <span className="flex flex-1 items-center justify-center gap-1.5">
                        <span className="font-mono font-bold">{store.storeCode}</span>
                        <span className="text-gray-600">
                          {store.city ?? store.storeName ?? "—"}
                          {store.state ? `/${store.state}` : ""}
                        </span>
                      </span>
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-center text-gray-400">
                    {regionByStore.get(store.storeCode) ?? "—"}
                  </TableCell>
                  <TableCell className="text-center tabular-nums">
                    {store.batchCount}
                  </TableCell>
                  <TableCell className="text-center tabular-nums">
                    {formatNumber(store.items)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-center tabular-nums",
                      store.notStarted > 0
                        ? "font-semibold text-red-600"
                        : "text-gray-400",
                    )}
                  >
                    {formatNumber(store.notStarted)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-center tabular-nums",
                      store.labels === 0 && "font-semibold text-red-600",
                    )}
                  >
                    {formatNumber(store.labels)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "whitespace-nowrap text-center",
                      !store.lastIssuedAt && "text-gray-400",
                    )}
                  >
                    {store.lastIssuedAt ?? "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-center">
                    <IdleCell days={store.daysSinceLastIssue} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-center">
                    <StoreStatusBadge status={store.status} />
                  </TableCell>
                  <TableCell className="text-center text-gray-300">›</TableCell>
                </TableRow>,
                isOpen ? (
                  <BatchLines
                    key={`${store.storeCode}-lotes`}
                    store={store}
                    onOpenStore={onOpenStore}
                  />
                ) : null,
              ];
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export default NetworkTable;
